import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs';
import { ProductReference } from 'src/app/models/layout/product-reference';
import { LayoutService } from 'src/app/services/layout.service';
import { PlanogramApiService } from 'src/app/services/planogram-api.service';
import { ToastService } from 'src/app/services/toasts.service';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-remove-products-modal',
    templateUrl: './remove-products-modal.component.html',
    styleUrl: './remove-products-modal.component.scss',
    imports: [FormsModule],
})
export class RemoveProductsModalComponent implements OnInit {
  public products: Map<number, ProductReference> = new Map<
    number,
    ProductReference
  >();
  public selectedProducts: Set<number> = new Set<number>();
  public filterText: string = '';
  public filteredProducts: ProductReference[] = [];
  private layoutBreakdown: Map<number, number> = new Map<number, number>();

  constructor(
    public activeModal: NgbActiveModal,
    private layoutService: LayoutService,
    private toastService: ToastService,
    private planogramApiService: PlanogramApiService
  ) {}

  ngOnInit(): void {
    this.products = this.layoutService.planogramItems;

    this.layoutService.layoutBreakdown$.pipe(take(1)).subscribe((breakdown) => {
      this.layoutBreakdown = breakdown;
      this.updateFilteredProducts();
    });
  }

  updateFilteredProducts(): void {
    const searchTerm = this.filterText.toLowerCase();

    this.filteredProducts = Array.from(this.products.values())
      // exclude products ON layout
      .filter(
        (product) => (this.layoutBreakdown.get(product.productId) ?? 0) === 0
      )
      // match search
      .filter(
        (product) =>
          product.description.toLowerCase().includes(searchTerm) ||
          product.barcode.toLowerCase().includes(searchTerm)
      );
  }

  onFilterChange(): void {
    this.updateFilteredProducts();
  }

  onToggleAll(event: Event): void {
    const checked = (event.target as HTMLInputElement | null)?.checked ?? false;
    this.toggleAll(checked);
  }

  toggleAll(selectAll: boolean): void {
    if (selectAll) {
      this.filteredProducts.forEach((p) =>
        this.selectedProducts.add(p.productId)
      );
    } else {
      this.filteredProducts.forEach((p) =>
        this.selectedProducts.delete(p.productId)
      );
    }
  }

  areAllSelected(): boolean {
    return (
      this.filteredProducts.length > 0 &&
      this.filteredProducts.every((p) => this.selectedProducts.has(p.productId))
    );
  }

  toggleProduct(productId: number): void {
    if (this.selectedProducts.has(productId)) {
      this.selectedProducts.delete(productId);
    } else {
      this.selectedProducts.add(productId);
    }
  }

  async removeSelectedProducts(): Promise<void> {
    if (this.selectedProducts.size === 0) return;

    let barcodes: string[] = [];
    this.selectedProducts.forEach((productId) =>
      barcodes.push(this.products.get(productId)?.barcode!)
    );

    const success = await this.planogramApiService.removeProducts(barcodes);
    if (success) {
      this.layoutService.removeProducts(Array.from(this.selectedProducts));
      this.toastService.show(
        `${barcodes.length} products removed successfully`,
        {
          classname: 'bg-success text-light p-3 fs-5',
          delay: 3000,
        }
      );
    } else {
      this.toastService.show('Products failed to be removed', {
        classname: 'bg-danger text-light p-3 fs-5',
        delay: 3000,
      });
    }
    this.activeModal.close(true);
  }

  cancel(): void {
    this.activeModal.close(null);
  }
}
